import { defaultSmDate } from 'utils/formatter';
import moment from 'moment-timezone';
import { QueryResult } from 'react-query';
import { callApi, Controller, useApi, UseApiOptions } from './useApi';
import {
    addLieferantenrechnungPosition,
    defaultLieferantenrechnungPosition,
    LieferantenrechnungPosition,
} from './lieferantenrechnungPosition';
// eslint-disable-next-line import/no-cycle
import { getKunde, Kunde } from './kunde';
// eslint-disable-next-line import/no-cycle
import { Auftrag } from './auftrag';
import { BestellungPosition } from './bestellungPosition';
import { Bestellung } from './bestellung';

export interface Lieferantenrechnung {
    kNr?: number;
    nr: string;
    auNr: string;
    status: number;
    datum: string;
    datBis: string;
    zahlBed: number;
    mwstCode: number;
    sNetto: number;
    sMwst: number;
    sBrutto: number;
    anlageDat?: string;
    anlageUhr?: number;
    anlageUser?: string;
    updDat?: string;
    updUhr?: number;
    updUser?: string;

    lieferant?: Kunde;
    auftrag?: Auftrag;
    isExported?: boolean;
}

export function getDefaultLieferantenrechnung(kNr?: number): Lieferantenrechnung {
    const values: Lieferantenrechnung = {
        kNr: undefined,
        nr: '',
        auNr: '',
        status: 0,
        datum: defaultSmDate,
        datBis: defaultSmDate,
        zahlBed: 1,
        mwstCode: 0,
        sNetto: 0,
        sMwst: 0,
        sBrutto: 0,
    };

    const now = moment();

    return {
        ...values,
        kNr,
        datum: now.format('YYYY-MM-DD') + 'T00:00:00',
        datBis: now.format('YYYY-MM-DD') + 'T00:00:00',
    };
}

export interface LieferantenrechnungFilter {
    nr?: string;
    nrSearch?: string;
    kNr?: number;
    auNr?: string;
    auNrSearch?: string;
    artikelGruppeNr?: number;
    artikelNr?: string;
    searchText?: string;
    datumVon?: string;
    datumBis?: string;
    anlageDatVon?: string;
    anlageDatBis?: string;
    status?: number;
    withLieferant?: boolean;
    withAuftrag?: boolean;
    maxRows?: number;
    sortAsc?: boolean;
}

export function useLieferantenrechnung(
    filter: LieferantenrechnungFilter,
    apiOptions?: UseApiOptions<Lieferantenrechnung>,
): QueryResult<Lieferantenrechnung> {
    return useApi<Lieferantenrechnung>(Controller.lieferantenrechnung + '/one', { filter }, apiOptions);
}

export function useLieferantenrechnungList(
    filter: LieferantenrechnungFilter,
    apiOptions?: UseApiOptions<Lieferantenrechnung[]>,
): QueryResult<Lieferantenrechnung[]> {
    return useApi<Lieferantenrechnung[]>(Controller.lieferantenrechnung + '/list', { filter }, apiOptions);
}

interface AddLieferantenrechnungResult {
    newNr: string;
}

export async function addLieferantenrechnung(
    lieferantenrechnung: Lieferantenrechnung,
): Promise<AddLieferantenrechnungResult> {
    return callApi<AddLieferantenrechnungResult>(Controller.lieferantenrechnung + '/add', { lieferantenrechnung });
}

export async function updateLieferantenrechnung(lieferantenrechnung: Lieferantenrechnung): Promise<void> {
    return callApi<void>(Controller.lieferantenrechnung + '/update', { lieferantenrechnung });
}

export async function deleteLieferantenrechnung(lieferantenrechnung: Lieferantenrechnung): Promise<void> {
    return callApi<void>(Controller.lieferantenrechnung + '/delete', { lieferantenrechnung });
}

export async function addLieferantenrechnungFromBestellung(
    kunde: Kunde,
    bestellung: Bestellung,
    bestellungPositionen?: BestellungPosition[],
): Promise<AddLieferantenrechnungResult> {
    const defaultLieferantenrechnung = getDefaultLieferantenrechnung();

    const kNr = bestellung.kNr || 0;

    const newLieferantenrechnung: Lieferantenrechnung = {
        ...defaultLieferantenrechnung,
        kNr,
        auNr: bestellung.auNr,
        zahlBed: bestellung.zahlBed,
        mwstCode: bestellung.mwstCode,
    };

    const result = await addLieferantenrechnung(newLieferantenrechnung);

    if (bestellungPositionen) {
        let pos = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const bestellungPosition of bestellungPositionen) {
            if (bestellungPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                await addLieferantenrechnungPosition({
                    ...defaultLieferantenrechnungPosition,
                    rNr: result.newNr,
                    pos,
                    artikelGruppeNr: bestellungPosition.artikelGruppeNr,
                    artikelNr: bestellungPosition.artikelNr,
                    posText: bestellungPosition.posText,
                    posEinh: bestellungPosition.posEinh,
                    posMenge: bestellungPosition.transferMenge || 0,
                    posEkPreis: bestellungPosition.posEkPreis,
                    posEPreis: bestellungPosition.posEPreis,
                    posNetto: bestellungPosition.posNetto,
                    posMwst: bestellungPosition.posMwst,
                    posBrutto: bestellungPosition.posBrutto,
                    posBem: bestellungPosition.posBem,
                });
                pos += 1;
            }
        }
    }

    return result;
}

export async function copyLieferantenrechnung(
    kNr: number,
    lieferantenrechnung: Lieferantenrechnung,
    lieferantenrechnungPositionen?: LieferantenrechnungPosition[],
): Promise<AddLieferantenrechnungResult> {
    const newKundeList = await getKunde({ nr: kNr });

    if (newKundeList.length !== 1) {
        throw new Error('Kunde nicht gefunden');
    }

    const newKunde = newKundeList[0];

    const defaultLieferantenrechnung = getDefaultLieferantenrechnung();

    const newLieferantenrechnung = {
        ...lieferantenrechnung,
        kNr,
        status: 0,
        datum: defaultLieferantenrechnung.datum,
        datBis: defaultLieferantenrechnung.datBis,
        zahlBed: newKunde.zahlZiel || defaultLieferantenrechnung.zahlBed,
        isExported: false,
    };

    const result = await addLieferantenrechnung(newLieferantenrechnung);

    if (lieferantenrechnungPositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const lieferantenrechnungPosition of lieferantenrechnungPositionen) {
            // eslint-disable-next-line no-await-in-loop
            await addLieferantenrechnungPosition({ ...lieferantenrechnungPosition, rNr: result.newNr });
        }
    }

    return result;
}

interface RecalculateLieferantenrechnungResult {
    lieferantenrechnung: Lieferantenrechnung;
}

export async function recalculateLieferantenrechnung(
    lieferantenrechnung: Lieferantenrechnung,
): Promise<RecalculateLieferantenrechnungResult> {
    return callApi<RecalculateLieferantenrechnungResult>(Controller.lieferantenrechnung + '/recalculate', {
        lieferantenrechnung,
    });
}
